import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Clock, Play, CheckCircle, BookOpen, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

const MyCourses = () => {
  const { t, i18n } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMyCourses();
  }, [i18n.language]);

  const fetchMyCourses = async () => {
    try {
      const response = await axios.get('/users/dashboard', {
        params: { lang: i18n.language }
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Erreur lors du chargement des cours:', error);
      setError(t('myCourses.errorLoading'));
      toast.error(t('myCourses.errorLoadingToast'));
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
    }
    return `${mins}min`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('myCourses.title')}</h1>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('myCourses.title')}</h1>
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={fetchMyCourses}
              className="btn btn-primary"
            >
              {t('myCourses.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('myCourses.title')}</h1>
          <Link
            to="/courses"
            className="btn btn-primary flex items-center"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            {t('myCourses.discoverMore')}
          </Link>
        </div>
        
        {courses.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('myCourses.noCourses')}</h3>
            <p className="text-gray-600 mb-6">
              {t('myCourses.noCoursesDesc')}
            </p>
            <Link 
              to="/courses"
              className="btn btn-primary"
            >
              {t('myCourses.browseCourses')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((courseData) => {
              const { course, progress, purchasedAt } = courseData;
              return (
                <div key={course.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  {/* Image du cours */}
                  <div className="h-48 bg-gradient-to-r from-primary-400 to-primary-600 relative">
                    {course.thumbnail ? (
                      <img 
                        src={course.thumbnail} 
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-white text-lg font-medium">{course.title}</span>
                      </div>
                    )}
                    
                    {/* Badge de progression */}
                    <div className="absolute top-4 right-4">
                      {progress.isCompleted ? (
                        <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {t('myCourses.completed')}
                        </div>
                      ) : (
                        <div className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                          {Math.round(progress.progressPercent)}%
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{course.title}</h3>
                    
                    {/* Barre de progression */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>{t('myCourses.progress')}</span>
                        <span>{Math.round(progress.progressPercent)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.round(progress.progressPercent)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Informations */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {formatDuration(course.duration)}
                      </div>
                      <div className="flex items-center">
                        <Play className="w-4 h-4 mr-1" />
                        {course.videoCount} {t('courses.videos')}
                      </div>
                    </div>

                    {/* Date d'achat */}
                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <Calendar className="w-4 h-4 mr-1" />
                      {t('myCourses.purchasedOn')} {formatDate(purchasedAt)}
                    </div>

                    {/* Bouton d'action */}
                    <Link
                      to={`/watch/${course.id}`}
                      className="w-full btn btn-primary flex items-center justify-center"
                    >
                      {progress.progressPercent > 0 ? t('myCourses.continue') : t('myCourses.start')}
                      <Play className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCourses;
